import { apiHosts } from "@/api/constants/apiHosts";
import type {
  ReleaseEvent,
  ReleaseEventNew,
  ReleaseEventProjectOfficerChange,
  ReleaseEventStatusChange,
} from "@/views/releaseEvent/releaseEvent";
import { getLookupsFromLookupGroup } from "@modernary/mui/components/lookup";
import { NotifyOptionsError, NotifyOptionsSuccess } from "@modernary/mui/components/notifications";
import { apiService, type Paging, type PagingApiAndNotifierConfig } from "@modernary/mui/utils/api";
import { serializeQueryParams } from "@modernary/mui/utils/helpers";
import type { AxiosResponse } from "axios";
import { getFacilityById } from "../facility/facilityApi";
import releaseEventService from "./releaseEventService";

const URL = "release-events";
const ENTITY_NAME = "Release Event";

const axiosParams = {
  // Set different base URL based on the environment
  baseURL: import.meta.env.VITE_UST_API_BASE_URL,
};

const api = apiService<ReleaseEvent, Paging<ReleaseEvent>>(apiHosts.UST, axiosParams);

/**
 * @param {Object} params Query parameters, varies (TODO improve this)
 * @param {abortCallback} config.abortMethod Callback for when a request has been aborted
 */
const getReleaseEvents = (params: any, config: PagingApiAndNotifierConfig = {}) => {
  //TODO:TS
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  const url = config.pagingUrl ? config.pagingUrl : URL;

  return api.getPaged(url, {
    params,
    paramsSerializer: serializeQueryParams,
    abort: config?.abortMethod,
    successNotifCb,
    errorNotifCb,
  });
};

const getReleaseEventById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.hide = true;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.fetched = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.get(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

const getNewReleaseEvent = async (facilityId?: string | number) => {
  if (facilityId) {
    return Promise.all([getFacilityById(facilityId)]).then(([result]) => {
      //TODO:TS This was previously hiding all notifs for this call. revisit
      const data = releaseEventService.getInitialData();
      data.facility = result.data;
      data.facilityId = result.data.id;

      return { data } as AxiosResponse<ReleaseEventNew, any>;
    });
  } else {
    const data = releaseEventService.getInitialData() as ReleaseEventNew;
    return { data } as AxiosResponse<ReleaseEventNew, any>;
  }
};

const createReleaseEvent = (releaseEvent: ReleaseEvent, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.created = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.created = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.post(URL, releaseEvent, {
    successNotifCb,
    errorNotifCb,
  });
};

const updateReleaseEvent = (releaseEvent: ReleaseEvent, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.put(URL + "/" + releaseEvent.id, releaseEvent, {
    successNotifCb,
    errorNotifCb,
  });
};

const confirmReleaseEvent = (releaseEvent: ReleaseEvent, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.put(URL + "/" + releaseEvent.id + "/confirm/", releaseEvent, {
    successNotifCb,
    errorNotifCb,
  });
};

const changeReleaseEventStatus = (
  releaseEventStatusUpdate: ReleaseEventStatusChange,
  config: PagingApiAndNotifierConfig = {},
) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.put(URL + "/" + releaseEventStatusUpdate.id + "/change-status/", releaseEventStatusUpdate, {
    successNotifCb,
    errorNotifCb,
  });
};

const changeReleaseEventProjectOfficer = (
  releaseEventProjectOfficerChange: ReleaseEventProjectOfficerChange,
  config: PagingApiAndNotifierConfig = {},
) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.updated = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.updated = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.put(
    URL + "/" + releaseEventProjectOfficerChange.id + "/change-project-officer/",
    releaseEventProjectOfficerChange,
    {
      successNotifCb,
      errorNotifCb,
    },
  );
};

/**
 * @param {ReleaseEvent} releaseEvent This cannot be a 'ref' to a release event
 */
const saveReleaseEvent = (releaseEvent: ReleaseEvent, config: PagingApiAndNotifierConfig = {}) => {
  if (releaseEvent.id) {
    return updateReleaseEvent(releaseEvent, config);
  } else {
    return createReleaseEvent(releaseEvent, config);
  }
};

const deleteReleaseEventById = (id: string | number, config: PagingApiAndNotifierConfig = {}) => {
  const successNotifCb = (options: NotifyOptionsSuccess) => {
    options.deleted = ENTITY_NAME;
    config?.successNotifCb?.(options);
  };
  const errorNotifCb = (options: NotifyOptionsError) => {
    options.deleted = ENTITY_NAME;
    config?.errorNotifCb?.(options);
  };

  return api.delete(URL + "/" + id, {
    successNotifCb,
    errorNotifCb,
  });
};

export {
  changeReleaseEventProjectOfficer,
  changeReleaseEventStatus,
  confirmReleaseEvent,
  createReleaseEvent,
  deleteReleaseEventById,
  getNewReleaseEvent,
  getReleaseEventById,
  getReleaseEvents,
  saveReleaseEvent,
  updateReleaseEvent,
};
